import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import { authToken } from '../globals/Global';

const API_BASE_URL = 'http://stg.panditjee.com:8080/api/banners';

// All available banner groups
const BANNER_GROUPS = [
  'EcommerceBanner',
  'HomeScreenBanner',
  'PromoSlider',
  'TempleBanner',
  'FestivalBanner',
  'Ecommerce',
  'MainBanner',
];

// All available locations
const BANNER_LOCATIONS = [
  'TempleTeerthYatraScreen',
  'PanditjeeHomeScreen',
  'EcommerceHomeScreen',
  'HomeScreen',
  'TempleBannerHomeScreen',
  'SHOP_SCREEN',
];

const defaultForm = {
  bannerGroup: 'EcommerceBanner',
  location: 'EcommerceHomeScreen',
  title: '',
  imageUrl: '',
  link: '',
  index: 0,
  status: 'active',
};

// Default state for the bulk update form
const defaultBulkForm = {
  title: '',
  link: '',
  status: 'active',
  index: '',
  image: null, // FILE
};

export default function BannerManagement() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refetch, setRefetch] = useState(0);
  const triggerRefetch = () => {
    setRefetch(prev => prev + 1);
  };

  // Filter states
  const [filterGroup, setFilterGroup] = useState('HomeScreenBanner');
  const [filterLocation, setFilterLocation] = useState('');

  // Dialog states
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openBulkUpdateDialog, setOpenBulkUpdateDialog] = useState(false);

  // Form & selection states
  const [newBanner, setNewBanner] = useState(defaultForm);
  const [editingBanner, setEditingBanner] = useState(null);
  const [deletingBanner, setDeletingBanner] = useState(null);

  // Bulk update state
  // bulkUpdateTab: 0 = by Group, 1 = by Location
  const [bulkUpdateTab, setBulkUpdateTab] = useState(0);
  const [bulkForm, setBulkForm] = useState(defaultBulkForm);
  const [bulkGroup, setBulkGroup] = useState('');
  const [bulkLocation, setBulkLocation] = useState('EcommerceHomeScreen');
  const [bulkLoading, setBulkLoading] = useState(false);

  const getAuthToken = () => authToken || localStorage.getItem('sessionToken');

  // Debug: remove after confirming token works
  useEffect(() => {
    const token = getAuthToken();
    console.log('BannerManagement token:', token);
  }, []);

  // --- Fetch Banners ---
  const fetchBanners = useCallback(async () => {
    try {
      setLoading(true);
      const token = getAuthToken();

      let url = `${API_BASE_URL}/list`;

      if (filterGroup) {
        url = `${API_BASE_URL}/group/${filterGroup}`;
      } else if (filterLocation) {
        url = `${API_BASE_URL}/location/${filterLocation}`;
      }

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch banners');
      const data = await res.json();

      setBanners(data?.data || data || []);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Error fetching banners');
    } finally {
      setLoading(false);
    }
  }, [filterGroup, filterLocation]);

  useEffect(() => {
    fetchBanners();
  }, [refetch, fetchBanners]);

  const handleClearFilters = () => {
  setFilterGroup('HomeScreenBanner');
  setFilterLocation('');
};

  // --- Create Banner ---
  const handleCreate = async () => {
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_BASE_URL}/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...newBanner, index: Number(newBanner.index) }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || 'Could not create banner.');
      toast.success('Banner created successfully!');
      setOpenCreateDialog(false);
      setNewBanner(defaultForm);
      triggerRefetch();
    } catch (error) {
      console.error('API error:', error);
      toast.error(`Error: ${error.message}`);
    }
  };

  // --- Update Banner ---
  const handleUpdate = async () => {
    if (!editingBanner) return;
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_BASE_URL}/update/${editingBanner.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...editingBanner, index: Number(editingBanner.index) }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || 'Could not update banner.');
      toast.success('Banner updated successfully!');
      setOpenEditDialog(false);
      setEditingBanner(null);
      triggerRefetch();
    } catch (error) {
      console.error('API error:', error);
      toast.error(`Error: ${error.message}`);
    }
  };

  // --- Delete Banner ---
  const handleDelete = async () => {
    if (!deletingBanner) return;
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_BASE_URL}/delete/${deletingBanner.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const result = await res.json();
        throw new Error(result.message || 'Could not delete banner.');
      }
      toast.success('Banner deleted successfully!');
      setOpenDeleteDialog(false);
      setDeletingBanner(null);
      triggerRefetch();
    } catch (error) {
      console.error('API error:', error);
      toast.error(`Error: ${error.message}`);
    }
  };

  // --- Bulk Update by Banner Group ---
  // PUT /api/banners/update/bannerGroup/{bannerGroup}
  // Body: multipart/form-data with title, link, bannerGroup, status, index, image (FILE)
  const handleBulkUpdateByGroup = async () => {
    setBulkLoading(true);
    try {
      const token = getAuthToken();

      const formData = new FormData();
      formData.append('title', bulkForm.title);
      formData.append('link', bulkForm.link);
      formData.append('bannerGroup', bulkGroup);
      formData.append('status', bulkForm.status);
      formData.append('index', String(bulkForm.index));
      if (bulkForm.image) {
        formData.append('image', bulkForm.image);
      }

      const res = await fetch(`${API_BASE_URL}/update/bannerGroup/${bulkGroup}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          // Do NOT set Content-Type manually; browser sets it with boundary for FormData
        },
        body: formData,
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.message || 'Could not bulk update banners by group.');

      toast.success(`Banners in group "${bulkGroup}" updated successfully!`);
      setOpenBulkUpdateDialog(false);
      setBulkForm(defaultBulkForm);
      triggerRefetch();
    } catch (error) {
      console.error('Bulk update (group) error:', error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setBulkLoading(false);
    }
  };

  // --- Bulk Update by Location ---
  // PUT /api/banners/update/location/{location}
  // Body: multipart/form-data with title, link, location, status, index, image (FILE)
  const handleBulkUpdateByLocation = async () => {
    setBulkLoading(true);
    try {
      const token = getAuthToken();

      const formData = new FormData();
      formData.append('title', bulkForm.title);
      formData.append('link', bulkForm.link);
      formData.append('location', bulkLocation);
      formData.append('status', bulkForm.status);
      formData.append('index', String(bulkForm.index));
      if (bulkForm.image) {
        formData.append('image', bulkForm.image);
      }

      const res = await fetch(`${API_BASE_URL}/update/location/${bulkLocation}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.message || 'Could not bulk update banners by location.');

      toast.success(`Banners at location "${bulkLocation}" updated successfully!`);
      setOpenBulkUpdateDialog(false);
      setBulkForm(defaultBulkForm);
      triggerRefetch();
    } catch (error) {
      console.error('Bulk update (location) error:', error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkSubmit = () => {
    if (bulkUpdateTab === 0) {
      handleBulkUpdateByGroup();
    } else {
      handleBulkUpdateByLocation();
    }
  };

  // Reset bulk dialog state on close
  const handleCloseBulkDialog = () => {
    setOpenBulkUpdateDialog(false);
    setBulkForm(defaultBulkForm);
    setBulkUpdateTab(0);
    setBulkGroup('EcommerceBanner');
    setBulkLocation('EcommerceHomeScreen');
  };

  // --- Open Handlers ---
  const openEdit = (banner) => {
    setEditingBanner({ ...banner });
    setOpenEditDialog(true);
  };

  const openDelete = (banner) => {
    setDeletingBanner(banner);
    setOpenDeleteDialog(true);
  };

  // --- Reusable Form Fields (for Create / Edit) ---
  const BannerFormFields = ({ data, onChange }) => (
    <Box>
      <TextField
        fullWidth
        label="Banner Title"
        margin="normal"
        value={data.title}
        onChange={e => onChange({ ...data, title: e.target.value })}
      />

      <Box sx={{ display: 'flex', gap: 2 }}>
        <FormControl fullWidth margin="normal">
          <InputLabel>Banner Group</InputLabel>
          <Select
            value={data.bannerGroup}
            label="Banner Group"
            onChange={e => onChange({ ...data, bannerGroup: e.target.value })}
          >
            {BANNER_GROUPS.map(group => (
              <MenuItem key={group} value={group}>
                {group}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <InputLabel>Location</InputLabel>
          <Select
            value={data.location}
            label="Location"
            onChange={e => onChange({ ...data, location: e.target.value })}
          >
            {BANNER_LOCATIONS.map(loc => (
              <MenuItem key={loc} value={loc}>
                {loc}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          fullWidth
          label="Display Order"
          type="number"
          margin="normal"
          value={data.index}
          inputProps={{ min: 0 }}
          onChange={e => onChange({ ...data, index: e.target.value })}
        />

        <FormControl fullWidth margin="normal">
          <InputLabel>Status</InputLabel>
          <Select
            value={data.status}
            label="Status"
            onChange={e => onChange({ ...data, status: e.target.value })}
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TextField
        fullWidth
        label="Image URL"
        margin="normal"
        value={data.imageUrl}
        onChange={e => onChange({ ...data, imageUrl: e.target.value })}
        placeholder="https://example.com/image.jpg"
      />

      <TextField
        fullWidth
        label="Link URL"
        margin="normal"
        value={data.link}
        onChange={e => onChange({ ...data, link: e.target.value })}
        placeholder="https://example.com/page"
      />

      {/* Image Preview */}
      {data.imageUrl && (
        <Box mt={2}>
          <Typography variant="body2" fontWeight="bold" mb={1}>
            Image Preview:
          </Typography>
          <Box
            component="img"
            src={data.imageUrl}
            alt="Preview"
            sx={{
              width: '100%',
              maxHeight: 200,
              objectFit: 'contain',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.300',
            }}
            onError={e => {
              e.target.style.display = 'none';
            }}
          />
        </Box>
      )}
    </Box>
  );

  // --- Bulk Update Form Fields ---
  const BulkUpdateFormFields = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {bulkUpdateTab === 0
          ? 'All banners in the selected group will be updated with the values below. Leave a field blank to skip updating it.'
          : 'All banners at the selected location will be updated with the values below. Leave a field blank to skip updating it.'}
      </Typography>

      {/* Group or Location selector */}
      {bulkUpdateTab === 0 ? (
        <FormControl fullWidth margin="normal">
          <InputLabel>Banner Group *</InputLabel>
          <Select
            value={bulkGroup}
            label="Banner Group *"
            onChange={e => setBulkGroup(e.target.value)}
          >
            <MenuItem value="">
              <em>Select Group</em>
            </MenuItem>
            {BANNER_GROUPS.map(group => (
              <MenuItem key={group} value={group}>
                {group}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <FormControl fullWidth margin="normal">
          <InputLabel>Location *</InputLabel>
          <Select
            value={bulkLocation}
            label="Location *"
            onChange={e => setBulkLocation(e.target.value)}
          >
            {BANNER_LOCATIONS.map(loc => (
              <MenuItem key={loc} value={loc}>
                {loc}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Divider sx={{ my: 1.5 }} />

      <TextField
        fullWidth
        label="Title"
        margin="normal"
        value={bulkForm.title}
        onChange={e => setBulkForm(prev => ({ ...prev, title: e.target.value }))}
      />

      <TextField
        fullWidth
        label="Link URL"
        margin="normal"
        value={bulkForm.link}
        onChange={e => setBulkForm(prev => ({ ...prev, link: e.target.value }))}
        placeholder="https://example.com/page"
      />

      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          fullWidth
          label="Display Order (index)"
          type="number"
          margin="normal"
          value={bulkForm.index}
          inputProps={{ min: 0 }}
          onChange={e => setBulkForm(prev => ({ ...prev, index: e.target.value }))}
        />

        <FormControl fullWidth margin="normal">
          <InputLabel>Status</InputLabel>
          <Select
            value={bulkForm.status}
            label="Status"
            onChange={e => setBulkForm(prev => ({ ...prev, status: e.target.value }))}
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* File Upload for image */}
      <Box mt={2}>
        <Typography variant="body2" fontWeight="bold" mb={1}>
          Upload Image (optional)
        </Typography>
        <Button
          variant="outlined"
          component="label"
          fullWidth
          sx={{ textTransform: 'none', justifyContent: 'flex-start', px: 2 }}
        >
          {bulkForm.image ? bulkForm.image.name : 'Choose image file…'}
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={e => {
              const file = e.target.files?.[0] || null;
              setBulkForm(prev => ({ ...prev, image: file }));
            }}
          />
        </Button>
        {bulkForm.image && (
          <Box mt={1.5}>
            <Typography variant="body2" fontWeight="bold" mb={0.5}>
              Preview:
            </Typography>
            <Box
              component="img"
              src={URL.createObjectURL(bulkForm.image)}
              alt="Selected"
              sx={{
                width: '100%',
                maxHeight: 180,
                objectFit: 'contain',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.300',
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );

  const hasActiveFilter = (filterGroup && filterGroup !== 'HomeScreenBanner') || filterLocation;

  return (
    <Box sx={{ height: '100vh', overflow: 'hidden', p: 2 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight="600">
          Banner Management
        </Typography>
        <Box display="flex" gap={1.5}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<SystemUpdateAltIcon />}
            onClick={() => setOpenBulkUpdateDialog(true)}
            sx={{ fontWeight: 'bold', textTransform: 'none' }}
          >
            Bulk Update
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenCreateDialog(true)}
            sx={{ fontWeight: 'bold', textTransform: 'none' }}
          >
            + Add New Banner
          </Button>
        </Box>
      </Box>

      {/* Banners Grid */}
      <Card sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

          {/* Title + Filter Row */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            mb={2}
          >
            <Typography variant="h6" fontWeight="bold">
              All Banners ({banners.length})
            </Typography>

            {/* Filter Controls */}
            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              <FilterListIcon color="action" fontSize="small" />

              {/* Filter by Group */}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Filter by Group</InputLabel>
                <Select
                  value={filterGroup}
                  label="Filter by Group"
                  onChange={e => {
                    setFilterGroup(e.target.value);
                    if (e.target.value) setFilterLocation('');
                  }}
                >
                  {BANNER_GROUPS.map(group => (
                    <MenuItem key={group} value={group}>
                      {group}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Filter by Location */}
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Filter by Location</InputLabel>
                <Select
                  value={filterLocation}
                  label="Filter by Location"
                  onChange={e => {
                    setFilterLocation(e.target.value);
                    if (e.target.value) setFilterGroup('');
                  }}
                >
                  {BANNER_LOCATIONS.map(loc => (
                    <MenuItem key={loc} value={loc}>
                      {loc}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Clear Filters Button */}
              {hasActiveFilter && (
                <IconButton
                  size="small"
                  onClick={handleClearFilters}
                  title="Clear filters"
                  color="default"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>

          {/* Active Filter Chips */}
          {hasActiveFilter && (
            <Box display="flex" gap={1} mb={1.5} flexWrap="wrap">
              {filterGroup && (
                <Chip
                  label={`Group: ${filterGroup}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  onDelete={() => setFilterGroup('')}
                />
              )}
              {filterLocation && (
                <Chip
                  label={`Location: ${filterLocation}`}
                  size="small"
                  color="secondary"
                  variant="outlined"
                  onDelete={() => setFilterLocation('')}
                />
              )}
            </Box>
          )}

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {loading && (
              <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                <CircularProgress />
              </Box>
            )}

            {!loading && banners.length === 0 && (
              <Typography sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
                {hasActiveFilter
                  ? 'No banners found for the selected filter.'
                  : 'No banners found. Create your first banner to get started!'}
              </Typography>
            )}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: 2,
              }}
            >
              {banners.map(banner => (
                <Box
                  key={banner.id}
                  sx={{
                    border: 1,
                    borderRadius: 2,
                    borderColor: 'grey.200',
                    overflow: 'hidden',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: 4 },
                  }}
                >
                  {/* Banner Image */}
                  <Box sx={{ position: 'relative', height: 180, bgcolor: 'grey.100' }}>
                    <Box
                      component="img"
                      src={banner.imageUrl || 'https://via.placeholder.com/320x180'}
                      alt={banner.title}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={e => {
                        e.target.src = 'https://via.placeholder.com/320x180';
                      }}
                    />
                    <Chip
                      label={banner.status}
                      size="small"
                      color={banner.status === 'active' ? 'success' : 'error'}
                      sx={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                      }}
                    />
                  </Box>

                  {/* Banner Info */}
                  <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      {banner.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Group:</strong> {banner.bannerGroup}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Location:</strong> {banner.location}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Order:</strong> {banner.index}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="primary"
                      sx={{
                        mt: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <strong>Link:</strong>{' '}
                      <a
                        href={banner.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'inherit' }}
                      >
                        {banner.link}
                      </a>
                    </Typography>
                  </Box>

                  {/* Actions */}
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    gap={1}
                    sx={{
                      px: 2,
                      pb: 1.5,
                      pt: 1,
                      borderTop: '1px solid',
                      borderColor: 'grey.100',
                    }}
                  >
                    <IconButton size="small" color="primary" onClick={() => openEdit(banner)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => openDelete(banner)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* --- Create Dialog --- */}
      <Dialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add New Banner</DialogTitle>
        <DialogContent>
          <BannerFormFields data={newBanner} onChange={setNewBanner} />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenCreateDialog(false);
              setNewBanner(defaultForm);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleCreate} variant="contained">
            Create Banner
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- Edit Dialog --- */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Banner</DialogTitle>
        {editingBanner && (
          <>
            <DialogContent>
              <BannerFormFields data={editingBanner} onChange={setEditingBanner} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
              <Button onClick={handleUpdate} variant="contained">
                Save Changes
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* --- Delete Confirm Dialog --- */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the banner:{' '}
            <strong>{deletingBanner?.title}</strong>?
          </Typography>
          <Typography color="error" variant="body2" mt={1}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- Bulk Update Dialog --- */}
      <Dialog
        open={openBulkUpdateDialog}
        onClose={handleCloseBulkDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Bulk Update Banners</DialogTitle>
        <DialogContent>
          {/* Tab switcher: by Group vs by Location */}
          <Tabs
            value={bulkUpdateTab}
            onChange={(_, newVal) => setBulkUpdateTab(newVal)}
            sx={{ mb: 1 }}
          >
            <Tab label="By Banner Group" />
            <Tab label="By Location" />
          </Tabs>
          <BulkUpdateFormFields />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkDialog} disabled={bulkLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkSubmit}
            variant="contained"
            color="secondary"
            disabled={bulkLoading}
            startIcon={bulkLoading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {bulkLoading ? 'Updating…' : 'Apply Bulk Update'}
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </Box>
  );
}